import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character, 
  CharacterRecipeComponent,
  Creature,
  copyCreatureDetails,
} from "@dndModels";
import { CHALLENGE_RATINGS, CREATURE_TYPES, RECIPE_COMPONENT_TYPES, SIZE_OPTIONS } from "@dndModels/Constants";
import { CreatureType, RecipeComponentType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import AddButton from "@components/input/buttons/AddButton";
import DeleteButton from "@components/input/buttons/DeleteButton";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";
import SelectCreatureBox from "@dndComponents/select/SelectCreatureBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import CheckBox from "@components/input/CheckBox";

interface Props {
  character: Character;
}

const api = DandDAPI.getInstance();

export default function CharacterManageRecipeComponents({
  character: char,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [working_on, setWorkingOn] = useState<CharacterRecipeComponent | null>(null);
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const disabled = !api.online || !(api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();

  if (working_on === null) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>Recipe Components</Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <AddButton
            disabled={disabled}
            onClick={() => {
              const comp = new CharacterRecipeComponent();
              char.recipe_components.push(comp);
              char.subscription_holder.itemsChanged();
              setWorkingOn(comp);
            }}
          />
        </Grid>
        { !reloading && char.recipe_components.map((component, key) => {
          return (
            <Grid
              key={key}
              className="padded"
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row"
                className={cx2(rowClasses.root)}>
                <Grid
                  className='clickable'
                  onClick={() => {
                    setWorkingOn(component);
                  }}
                  size={{
                    xs: 9
                  }}>
                  { component.use_name }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DeleteButton
                    disabled={disabled}
                    onClick={() => {
                      char.recipe_components = char.recipe_components.filter(o => o.true_id !== component.true_id);
                      char.subscription_holder.itemsChanged();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    );
  } else {
    const component = working_on;
    // let possible_crs: DisablableOption[] = [];
    // if (component.creature) {
    //   let min = component.creature.min_challenge_rating;
    //   const max = component.creature.max_challenge_rating;
    //   possible_crs = CHALLENGE_RATINGS.filter(o => (+o.id) >= min && (+o.id) <= max);
    // }
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <ButtonBox
            name="Back"
            onClick={() => {
              setWorkingOn(null);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectCreatureBox
            name="Creature"
            disabled={disabled}
            value={component.creature_details._id}
            onChangeObject={(changed: Creature) => {
              // debugger;
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                copyCreatureDetails(changed, comp.creature_details);
                comp.creature = changed;
                comp.cr = changed.min_challenge_rating;
              }
              char.subscription_holder.itemsChanged();
              setReloading(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          { !reloading && 
            <SelectStringBox
              name="Challenge Rating of Creature"
              options={CHALLENGE_RATINGS}
              value={`${component.cr}`}
              disabled={disabled}
              onChange={(changed: string) => {
                const comp = char.recipe_components.find(o => o.true_id === component.true_id);
                if (comp) {
                  comp.cr = +changed;
                }
                char.subscription_holder.itemsChanged();
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Component Type"
            options={RECIPE_COMPONENT_TYPES}
            value={component.component_type}
            disabled={disabled}
            onChange={(changed: RecipeComponentType) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.component_type = changed;
                if (comp.name === "") {
                  comp.name = comp.component_type;
                }
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox
            name="Component Name"
            value={component.name}
            disabled={disabled}
            onBlur={(changed: string) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.name = changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Creature Type"
            options={CREATURE_TYPES}
            value={component.creature_details.creature_type}
            onChange={(changed: CreatureType) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.creature_details.creature_type = changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SuggestionStringBox 
            name="Subtype"
            field="subtype"
            values={component.creature_details.subtypes}
            disabled={disabled}
            onChange={(changed: string[]) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.creature_details.subtypes = changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Size"
            options={SIZE_OPTIONS}
            value={`${component.creature_details.size_num}`}
            onChange={(changed: string) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.creature_details.size_num = +changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox
            name="Flying"
            value={component.creature_details.flying}
            onChange={(changed: boolean) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.creature_details.flying = changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox
            name="Swimming"
            value={component.creature_details.swimming}
            onChange={(changed: boolean) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.creature_details.swimming = changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Description"
            value={component.description}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const comp = char.recipe_components.find(o => o.true_id === component.true_id);
              if (comp) {
                comp.description = changed;
              }
              char.subscription_holder.itemsChanged();
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
